<template>
  <div class="payment-page">
    <!-- Кнопка домой -->
    <HomeButton />

    <!-- Выбор сервера (показываем только для новых подписок) -->
    <ServerSelection
      v-if="showServerSelection"
      @server-selected="handleServerSelected"
      @back="handleBack"
    />

    <!-- Загрузка серверов для новых подписок -->
    <div
      v-else-if="purchaseType === 'new' && serverStore.isLoading"
      class="payment-page__loading"
    >
      <div class="payment-page__header">
        <h1 class="payment-page__title">Покупка подписки</h1>
        <p class="payment-page__subtitle">Загружаем доступные серверы...</p>
      </div>
      <Loader />
    </div>

    <!-- Ошибка загрузки серверов -->
    <div
      v-else-if="purchaseType === 'new' && serverStore.error"
      class="payment-page__error"
    >
      <div class="payment-page__header">
        <h1 class="payment-page__title">Покупка подписки</h1>
        <p class="payment-page__subtitle">Ошибка загрузки серверов</p>
      </div>
      <p>{{ serverStore.error }}</p>
      <BaseButton
        @click="handleRetryServers"
        styles="secondary-200"
        text="Попробовать снова"
      />
    </div>

    <!-- Нет доступных серверов -->
    <div
      v-else-if="
        purchaseType === 'new' &&
        !serverStore.isLoading &&
        serverStore.servers.length === 0
      "
      class="payment-page__error"
    >
      <div class="payment-page__header">
        <h1 class="payment-page__title">Покупка подписки</h1>
        <p class="payment-page__subtitle">Сервера недоступны</p>
      </div>
      <p>В данный момент нет доступных серверов для покупки подписки</p>
      <BaseButton
        @click="handleRetryServers"
        styles="secondary-200"
        text="Обновить список"
      />
    </div>

    <!-- Выбор тарифа (показываем после выбора сервера или для продления) -->
    <div v-else-if="showTariffSelection" class="payment-page__content">
      <div class="payment-page__header">
        <h1 class="payment-page__title">
          {{ isExtension ? "Продление подписки" : "Покупка подписки" }}
        </h1>
        <p class="payment-page__subtitle">
          Выберите продолжительность подписки
        </p>
      </div>

      <div class="payment-page__autopayment-mode">
        <button
          :class="[
            'payment-page__mode-option',
            { 'payment-page__mode-option--selected': autopaymentEnabled },
          ]"
          @click="setAutopaymentMode(true)"
        >
          С автопродлением
        </button>
        <button
          :class="[
            'payment-page__mode-option',
            { 'payment-page__mode-option--selected': !autopaymentEnabled },
          ]"
          @click="setAutopaymentMode(false)"
        >
          Без автопродления
        </button>
      </div>
      <!-- <p v-if="isModeSwitchLoading" class="payment-page__mode-hint">
        Обновляем цены...
      </p> -->

      <div class="payment-page__main">
        <div v-if="showInitialTariffsLoading" class="payment-page__loading">
          <Loader />
        </div>

        <div
          v-else-if="unavailableOperationMessage"
          class="payment-page__error"
        >
          <p>{{ unavailableOperationMessage }}</p>
          <BaseButton
            text="Вернуться к выбору периода"
            styles="secondary-200"
            @click="handleReturnToTariffSelection"
          />
        </div>

        <div v-else-if="paymentStore.error" class="payment-page__error">
          <p>Ошибка загрузки тарифов: {{ paymentStore.error }}</p>
        </div>

        <div
          v-else
          :class="[
            'payment-page__tariffs',
            { 'payment-page__tariffs--updating': isModeSwitchLoading },
          ]"
        >
          <div
            v-for="(tariff, index) in paymentStore.tariffs"
            :key="index"
            :class="[
              'payment-page__tariff-card',
              {
                'payment-page__tariff-card--selected':
                  paymentStore.selectedTariff?.months === tariff.months,
                'payment-page__tariff-card--popular':
                  getTariffLabel(tariff.months) === 'ПОПУЛЯРНЫЙ',
                'payment-page__tariff-card--profitable':
                  getTariffLabel(tariff.months) === 'ВЫГОДНЫЙ',
              },
            ]"
            @click="paymentStore.selectTariff(tariff)"
          >
            <div
              v-if="getTariffLabel(tariff.months)"
              class="payment-page__tariff-label"
            >
              {{ getTariffLabel(tariff.months) }}
            </div>

            <BaseTariff
              :months="tariff.months"
              :price="tariff.price"
              :per-month="tariff.per_month"
              :subtitle="getTariffTitle(tariff.months)"
            />
          </div>
        </div>

        <div class="payment-page__footer">
          <BaseButton
            text="К оформлению"
            styles="primary-gradient"
            :is-disabled="!paymentStore.selectedTariff"
            @on-submit="handlePurchase"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  computed,
  watch,
} from "vue";
import { usePaymentStore } from "@/entities/Payment/model/paymentStore";
import { useSubscriptionStore } from "@/entities/Subscription/model/subscriptionStore";
import { useServerStore } from "@/entities/Server";
import type {
  PaymentMethod,
  PaymentTariff,
} from "@/entities/Payment/model/types";
import type { Server } from "@/entities/Server";
import { PaymentMethodCode } from "@/entities/Payment/model/types";
import { useUserStore } from "@/stores/useUser";
import BaseTariff from "@/ui/components/Base/BaseTariff.vue";
import BaseButton from "@/ui/components/Base/BaseButton.vue";
import Loader from "@/ui/components/Loader.vue";
import ServerSelection from "@/ui/components/ServerSelection.vue";
import HomeButton from "@/ui/components/HomeButton.vue";
import { useVibration } from "@/shared/utils/system/vibration";
import router from "@/router";
import { useModal } from "@/shared/lib/modal/useModal";
import { SentryService } from "@/shared/lib/sentry/sentryService";

const paymentStore = usePaymentStore();
const subscriptionStore = useSubscriptionStore();
const serverStore = useServerStore();
const userStore = useUserStore();
const { openSubscriptionModal, closeAllModals } = useModal();
const { vibratePattern } = useVibration();

// Флаг для предотвращения двойных запросов
const isProcessingPayment = ref(false);

// Флаг для контроля перехода к тарифам после выбора сервера
const userConfirmedServerSelection = ref(false);

// Определяем тип операции: новая подписка или продление
const purchaseType = ref<"new" | "extension">("new");
const autopaymentEnabled = ref(true);
const unavailableOperationMessage = ref<string | null>(null);
const isModeSwitchLoading = ref(false);

// Computed properties
const isExtension = computed(() => purchaseType.value === "extension");
const showInitialTariffsLoading = computed(
  () => paymentStore.isLoading && paymentStore.tariffs.length === 0,
);
const showServerSelection = computed(() => {
  // Показываем выбор сервера для новых подписок, если сервер не выбран и серверы загружены
  const shouldShow =
    purchaseType.value === "new" &&
    !serverStore.selectedServer &&
    !serverStore.isLoading &&
    serverStore.servers.length > 0;

  console.log("showServerSelection computed:", {
    purchaseType: purchaseType.value,
    selectedServer: serverStore.selectedServer,
    isLoading: serverStore.isLoading,
    serversCount: serverStore.servers.length,
    shouldShow,
  });

  return shouldShow;
});

const showTariffSelection = computed(() => {
  // Показываем выбор тарифов если:
  // 1. Это продление подписки (сервер уже выбран)
  // 2. Это новая подписка И сервер выбран И пользователь подтвердил выбор
  const shouldShow =
    isExtension.value ||
    (purchaseType.value === "new" &&
      serverStore.selectedServer !== null &&
      userConfirmedServerSelection.value);

  console.log("showTariffSelection computed:", {
    purchaseType: purchaseType.value,
    isExtension: isExtension.value,
    selectedServer: serverStore.selectedServer,
    userConfirmedServerSelection: userConfirmedServerSelection.value,
    shouldShow,
  });

  return shouldShow;
});

async function loadTariffsByFlow(): Promise<void> {
  unavailableOperationMessage.value = null;

  if (isExtension.value && subscriptionStore.subscription?.id) {
    console.log(
      "🔄 Загружаем тарифы для продления:",
      subscriptionStore.subscription.id,
      "autopayment:",
      autopaymentEnabled.value,
    );
    await paymentStore.fetchTariffs(
      subscriptionStore.subscription.id,
      autopaymentEnabled.value,
    );
    return;
  }

  if (purchaseType.value === "new" && serverStore.selectedServer) {
    console.log(
      "🆕 Загружаем тарифы для новой подписки на сервере:",
      serverStore.selectedServer.name,
      "autopayment:",
      autopaymentEnabled.value,
    );
    await paymentStore.fetchTariffs(undefined, autopaymentEnabled.value);
  }
}

function setAutopaymentMode(value: boolean) {
  if (autopaymentEnabled.value === value) {
    return;
  }

  autopaymentEnabled.value = value;
}

// Watcher для загрузки тарифов при переходе к экрану выбора тарифов
watch(showTariffSelection, async (newValue: boolean) => {
  if (newValue) {
    console.log("🎯 Переход к экрану выбора тарифов, загружаем тарифы...");
    await loadTariffsByFlow();
  }
});

watch(autopaymentEnabled, async () => {
  if (!showTariffSelection.value) {
    return;
  }

  isModeSwitchLoading.value = true;
  unavailableOperationMessage.value = null;

  try {
    await loadTariffsByFlow();
  } finally {
    isModeSwitchLoading.value = false;
  }
});

onMounted(() => {
  // Инициализируем Telegram Web App если доступен
  if (window.Telegram?.WebApp) {
    window.Telegram.WebApp.ready();
    window.Telegram.WebApp.expand();
  }
});

// Останавливаем проверку статуса при уходе со страницы
onBeforeUnmount(() => {
  console.log("Страница закрывается, останавливаем проверку статуса оплаты");

  // Логируем уход со страницы во время процесса оплаты
  if (isProcessingPayment.value || paymentStore.isCheckingPayment) {
    SentryService.setContext("payment_interrupted_page_leave", {
      reason: "page_unmount",
      tariff: paymentStore.selectedTariff
        ? {
            id: paymentStore.selectedTariff.id,
            name: paymentStore.selectedTariff.name,
            months: paymentStore.selectedTariff.months,
            price: paymentStore.selectedTariff.price,
          }
        : null,
      payment_method: paymentStore.selectedPaymentMethod
        ? {
            id: paymentStore.selectedPaymentMethod.id,
            name: paymentStore.selectedPaymentMethod.name,
            method_code: paymentStore.selectedPaymentMethod.method_code,
          }
        : null,
      bill_id: paymentStore.currentBillId,
      was_processing: isProcessingPayment.value,
      was_checking_status: paymentStore.isCheckingPayment,
    });

    SentryService.captureMessage(
      "Процесс оплаты прерван - пользователь покинул страницу",
      "warning",
    );
  }

  paymentStore.stopPaymentStatusPolling();
  isProcessingPayment.value = false;
});

// onMounted(async () => {
//   try {
//     await paymentStore.fetchPaymentMethods(1000)
//     console.log(paymentStore.paymentMethods)
//   } catch(e) {
//     console.log(e)
//   }
// })

// Перенаправляем на главную при прямом заходе на страницу или обновлении
onBeforeMount(async () => {
  console.log("PaymentPage onBeforeMount started");
  autopaymentEnabled.value = true;
  isModeSwitchLoading.value = false;
  unavailableOperationMessage.value = null;
  paymentStore.clearTariffs();
  paymentStore.clearPaymentMethods();

  // Проверяем, есть ли флаг о том, что пользователь пришел с главной страницы
  const cameFromHome = sessionStorage.getItem("cameFromHome");
  console.log("cameFromHome flag:", cameFromHome);

  if (!cameFromHome) {
    router.push("/");
    return;
  }

  // Очищаем флаг после использования
  sessionStorage.removeItem("cameFromHome");

  if (!userStore.user?.email && !userStore.isLoading) {
    try {
      await userStore.fetchUserInfo();
    } catch (error) {
      console.warn("Не удалось обновить профиль пользователя:", error);
    }
  }

  // Определяем тип операции
  const storedPurchaseType = sessionStorage.getItem("purchaseType");
  console.log("storedPurchaseType:", storedPurchaseType);
  purchaseType.value = storedPurchaseType === "extension" ? "extension" : "new";
  console.log("purchaseType set to:", purchaseType.value);

  // Дополнительная проверка: если purchaseType = 'new', но в store есть подписка,
  // возможно это продление (пользователь перешел не через правильный флаг)
  if (purchaseType.value === "new" && subscriptionStore.subscription?.id) {
    console.log(
      "🤔 ПОДОЗРЕНИЕ: purchaseType=new, но есть subscription в store:",
      subscriptionStore.subscription.id,
    );
    console.log(
      "🤔 Возможно, это продление. Проверяем URL или другие индикаторы...",
    );

    // Если в URL есть subscription_id или мы пришли с MySubscription, это продление
    const urlParams = new URLSearchParams(window.location.search);
    const urlSubscriptionId = urlParams.get("subscription_id");

    if (urlSubscriptionId || window.location.pathname.includes("extend")) {
      console.log("🔄 ИСПРАВЛЕНИЕ: Переключаем на режим продления");
      purchaseType.value = "extension";
    }
  }

  // Очищаем флаг типа покупки
  sessionStorage.removeItem("purchaseType");

  // Очищаем состояние выбора сервера при каждом новом заходе на страницу покупки
  console.log(
    "🧹 Очищаем состояние выбора сервера для нового процесса покупки",
  );
  userConfirmedServerSelection.value = false;

  // Для новых подписок очищаем выбранный сервер
  if (purchaseType.value === "new") {
    serverStore.clearSelection();
    console.log("🧹 Очищен выбранный сервер для новой подписки");
  }

  // Если это продление, проверяем что подписка уже установлена (не загружаем заново!)
  if (isExtension.value) {
    console.log("🔄 РЕЖИМ ПРОДЛЕНИЯ: Проверяем установленную подписку...");

    if (!subscriptionStore.subscription) {
      console.log("🔄 Подписка не установлена, загружаем все подписки...");
      await subscriptionStore.fetchSubscription(true); // загружаем все подписки
    } else {
      console.log(
        "🔄 Подписка уже установлена:",
        subscriptionStore.subscription,
      );
    }

    // Тарифы будут загружены автоматически через watcher когда showTariffSelection станет true
    console.log(
      "🔄 Тарифы будут загружены автоматически при переходе к выбору тарифов",
    );
  } else {
    // Для новых подписок загружаем список серверов
    console.log("🆕 РЕЖИМ НОВОЙ ПОДПИСКИ: Загружаем серверы...");
    try {
      await serverStore.fetchServers();
      console.log(
        "🆕 Загружены серверы:",
        serverStore.servers.length,
        serverStore.servers,
      );
      console.log("🆕 Server store state after fetch:", {
        isLoading: serverStore.isLoading,
        error: serverStore.error,
        selectedServer: serverStore.selectedServer,
      });
    } catch (error) {
      console.error("🆕 ❌ Ошибка загрузки серверов:", error);
    }
  }
});

// Обработчики событий
async function handleRetryServers() {
  await vibratePattern("button");
  await serverStore.fetchServers();
}

function handleServerSelected(server: Server) {
  console.log("Пользователь подтвердил выбор сервера:", server);
  serverStore.selectServer(server);

  // Устанавливаем флаг подтверждения выбора сервера
  userConfirmedServerSelection.value = true;

  console.log("Сервер выбран и подтвержден, переходим к выбору тарифов");
}

function getTariffTitle(months: number): string {
  if (months === 1) return "1 месяц";
  if (months === 3) return "3 месяца";
  if (months === 6) return "6 месяцев";
  if (months === 12) return "1 год";
  if (months === 24) return "2 года";
  if (months === 48) return "4 года";
  return `${months} мес.`;
}

function getTariffLabel(months: number): string | null {
  if (months === 6) return "ПОПУЛЯРНЫЙ";
  if (months === 12) return "ВЫГОДНЫЙ";
  return null;
}

function openPaymentModal(
  tariff: PaymentTariff,
  options?: {
    forceEmailInput?: boolean;
    emailErrorMessage?: string;
    paymentErrorMessage?: string;
  },
) {
  let startDate: Date | undefined;
  if (isExtension.value && subscriptionStore.subscription?.exp_date) {
    const expDate = new Date(subscriptionStore.subscription.exp_date);
    const now = new Date();
    startDate = expDate.getTime() > now.getTime() ? expDate : now;
  }

  openSubscriptionModal({
    period: getTariffTitle(tariff.months),
    endDate: calculateEndDate(tariff.months, startDate),
    price: tariff.price,
    months: tariff.months,
    autopaymentEnabled: autopaymentEnabled.value,
    subscriptionId: isExtension.value
      ? subscriptionStore.subscription?.id
      : undefined,
    forceEmailInput: options?.forceEmailInput ?? false,
    emailErrorMessage: options?.emailErrorMessage,
    paymentErrorMessage: options?.paymentErrorMessage,
    onPayment: async (method: PaymentMethod, email?: string) => {
      await processPayment(method, email, tariff);
    },
    onClose: () => {
      console.log("Модалка закрыта, останавливаем проверку статуса оплаты");

      SentryService.setContext("payment_cancelled_by_user", {
        reason: "modal_closed",
        tariff: paymentStore.selectedTariff
          ? {
              id: paymentStore.selectedTariff.id,
              name: paymentStore.selectedTariff.name,
              months: paymentStore.selectedTariff.months,
              price: paymentStore.selectedTariff.price,
            }
          : null,
        payment_method: paymentStore.selectedPaymentMethod
          ? {
              id: paymentStore.selectedPaymentMethod.id,
              name: paymentStore.selectedPaymentMethod.name,
              method_code: paymentStore.selectedPaymentMethod.method_code,
            }
          : null,
        bill_id: paymentStore.currentBillId,
        was_processing: isProcessingPayment.value,
      });

      SentryService.captureMessage(
        "Пользователь отменил оплату (закрыл модалку)",
        "info",
      );

      paymentStore.stopPaymentStatusPolling();
      isProcessingPayment.value = false;
    },
  });
}

async function handlePurchase() {
  if (!paymentStore.selectedTariff) return;
  unavailableOperationMessage.value = null;
  paymentStore.errorStatus = null;

  console.log("Selected tariff price:", paymentStore.selectedTariff.price);

  // Сначала загружаем методы оплаты
  try {
    await paymentStore.fetchPaymentMethods({
      months: paymentStore.selectedTariff.months,
      autopayment_enabled: autopaymentEnabled.value,
      subscription_id: isExtension.value
        ? subscriptionStore.subscription?.id
        : undefined,
    });
  } catch (e) {
    console.error("Ошибка загрузки методов оплаты:", e);
    return;
  }

  const tariff = paymentStore.selectedTariff;
  openPaymentModal(tariff);
}

function extractErrorDetail(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    (error as { data?: { detail?: unknown } }).data
  ) {
    const detail = (error as { data?: { detail?: unknown } }).data?.detail;
    if (typeof detail === "string") {
      return detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "";
}

function isEmailRequiredError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return false;
  }

  const status = (error as { status?: number }).status;
  const detail = extractErrorDetail(error).toLowerCase();
  return status === 400 && detail.includes("email");
}

// Выносим логику обработки платежа в отдельную функцию
async function processPayment(
  method: PaymentMethod,
  email: string | undefined,
  tariff: PaymentTariff,
) {
  // Предотвращаем двойные запросы
  if (isProcessingPayment.value) {
    console.log("Платеж уже обрабатывается, игнорируем повторный запрос");
    return;
  }

  isProcessingPayment.value = true;

  // Логируем начало обработки платежа
  SentryService.setContext("payment_processing_started", {
    tariff: {
      id: tariff.id,
      name: tariff.name,
      months: tariff.months,
      price: tariff.price,
    },
    payment_method: {
      id: method.id,
      name: method.name,
      method_code: method.method_code,
    },
    email: email || null,
    subscription_id: subscriptionStore.subscription?.id || null,
    server_id: isExtension.value
      ? subscriptionStore.subscription?.server?.id || null
      : serverStore.selectedServer?.id || null, // Для новых подписок используем выбранный сервер
    is_extension: isExtension.value,
    selected_server: serverStore.selectedServer
      ? {
          id: serverStore.selectedServer.id,
          name: serverStore.selectedServer.name,
          protocol: serverStore.selectedServer.protocol,
        }
      : null,
  });

  SentryService.captureMessage("Начата обработка платежа", "info");

  console.log("Выбранный метод оплаты:", method);
  if (email) {
    console.log("Email для чека:", email);
  }

  try {
    // Для продления: проверяем, что данные о подписке загружены (нужен subscription_id)
    // Для новой покупки: проверяем, что выбран сервер
    if (isExtension.value) {
      if (!subscriptionStore.subscription?.id) {
        console.warn(
          "Subscription ID не найден для продления, пытаемся загрузить подписку...",
        );
        await subscriptionStore.fetchSubscription();

        if (!subscriptionStore.subscription?.id) {
          throw new Error("Не удалось получить данные подписки для продления");
        }
      }
    } else {
      // Для новой подписки нужен выбранный сервер
      if (!serverStore.selectedServer?.id) {
        throw new Error("Не выбран сервер для новой подписки");
      }
    }

    // Функция для получения правильного кода метода оплаты
    const getMethodCode = (method: PaymentMethod): string => {
      // Если есть явный method_code, используем его
      if (method.method_code) {
        return method.method_code;
      }

      // Иначе пытаемся определить по имени или id
      const methodName = method.name.toLowerCase();
      const methodId = method.id.toLowerCase();

      if (methodName.includes("yookassa") || methodId.includes("yookassa")) {
        return PaymentMethodCode.YOOKASSA;
      }
      if (
        methodName.includes("tbank") ||
        methodName.includes("т-банк") ||
        methodId.includes("tbank")
      ) {
        return PaymentMethodCode.TBANK;
      }
      if (
        methodName.includes("balance") ||
        methodName.includes("баланс") ||
        methodId.includes("balance")
      ) {
        return PaymentMethodCode.BALANCE;
      }

      // Fallback - возвращаем исходное значение
      return method.id || method.name;
    };

    // Создаем данные для счета согласно API (CreateBillRequest)
    const billData: {
      months: number;
      method: string;
      autopayment_enabled: boolean;
      subscription_id?: string;
      server?: number;
    } = {
      months: tariff.months,
      method: getMethodCode(method),
      autopayment_enabled: autopaymentEnabled.value,
    };

    // Добавляем subscription_id для продления
    if (isExtension.value && subscriptionStore.subscription?.id) {
      billData.subscription_id = subscriptionStore.subscription.id;
      console.log(
        "🔄 ПРОДЛЕНИЕ: Добавлен subscription_id:",
        subscriptionStore.subscription.id,
      );
    }

    // Добавляем server для новой подписки
    if (!isExtension.value && serverStore.selectedServer?.id) {
      billData.server = serverStore.selectedServer.id;
      console.log(
        "🆕 НОВАЯ ПОДПИСКА: Добавлен server:",
        serverStore.selectedServer.id,
      );
    }

    console.log("📤 Отправляем данные для создания счета:", {
      isExtension: isExtension.value,
      billData,
      subscriptionId: subscriptionStore.subscription?.id,
      selectedServerId: serverStore.selectedServer?.id,
    });

    // Создаем счет на оплату
    const result = await paymentStore.createPaymentBill(billData);

    console.log("Результат создания счета:", result);

    // Обрабатываем результат
    if ("success" in result && result.success) {
      // Оплата с баланса прошла успешно - проверяем статус
      console.log("Оплата с баланса, начинаем проверку статуса");

      // Логируем успешную оплату с баланса
      SentryService.setContext("payment_balance_success", {
        bill_id: "bill_id" in result ? result.bill_id : null,
        tariff: {
          id: tariff.id,
          name: tariff.name,
          months: tariff.months,
          price: tariff.price,
        },
        payment_method: {
          id: method.id,
          name: method.name,
          method_code: method.method_code,
        },
      });

      SentryService.captureMessage(
        "Оплата с баланса выполнена успешно",
        "info",
      );

      if (result.bill_id) {
        startPaymentStatusCheck(result.bill_id);
      } else {
        // Если нет bill_id, считаем что оплата прошла успешно
        handlePaymentSuccess();
      }
    } else if ("url" in result) {
      const validPaymentUrl =
        typeof result.url === "string" ? result.url.trim() : "";

      if (!result.bill_id) {
        isProcessingPayment.value = false;
        closeAllModals();
        openPaymentModal(tariff, {
          paymentErrorMessage:
            "Не удалось начать отслеживание оплаты: отсутствует идентификатор счёта.",
        });
        return;
      }

      if (!validPaymentUrl) {
        isProcessingPayment.value = false;
        closeAllModals();
        openPaymentModal(tariff, {
          paymentErrorMessage:
            "Не удалось перейти к оплате: не получена ссылка на платежную страницу.",
        });
        return;
      }

      // Перенаправляем на платежную систему и начинаем проверку статуса
      console.log("Перенаправление на оплату:", validPaymentUrl);

      // Логируем перенаправление на внешнюю платежную систему
      SentryService.setContext("payment_external_redirect", {
        bill_id: result.bill_id,
        payment_url: validPaymentUrl,
        tariff: {
          id: tariff.id,
          name: tariff.name,
          months: tariff.months,
          price: tariff.price,
        },
        payment_method: {
          id: method.id,
          name: method.name,
          method_code: method.method_code,
        },
      });

      SentryService.captureMessage(
        "Перенаправление на внешнюю платежную систему",
        "info",
      );

      // Начинаем проверку статуса оплаты
      startPaymentStatusCheck(result.bill_id);

      // Используем Telegram Web App API для открытия внешних ссылок
      if (window.Telegram?.WebApp) {
        try {
          window.Telegram.WebApp.openLink(validPaymentUrl);
        } catch (error) {
          console.warn(
            "Ошибка при использовании Telegram WebApp openLink, используем window.open:",
            error,
          );
          // Fallback - открываем в новой вкладке
          window.open(validPaymentUrl, "_blank");
        }
      } else {
        // Fallback для обычных браузеров - открываем в новой вкладке
        window.open(validPaymentUrl, "_blank");
      }
    } else {
      console.error("Неожиданный формат ответа:", result);

      // Логируем неожиданный формат ответа
      SentryService.setContext("payment_unexpected_response", {
        response: result,
        tariff: {
          id: tariff.id,
          name: tariff.name,
          months: tariff.months,
          price: tariff.price,
        },
        payment_method: {
          id: method.id,
          name: method.name,
          method_code: method.method_code,
        },
      });

      SentryService.captureMessage(
        "Получен неожиданный формат ответа при создании счета",
        "error",
      );

      handlePaymentError("Неизвестная ошибка при создании счета");
    }
  } catch (error) {
    console.error("Ошибка при создании счета:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Ошибка при создании счета";

    // Логируем ошибку создания счета
    SentryService.setContext("payment_bill_creation_error", {
      error_message: errorMessage,
      error_type:
        error instanceof Error ? error.constructor.name : typeof error,
      tariff: {
        id: tariff.id,
        name: tariff.name,
        months: tariff.months,
        price: tariff.price,
      },
      payment_method: {
        id: method.id,
        name: method.name,
        method_code: method.method_code,
      },
    });

    SentryService.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );

    if (isEmailRequiredError(error)) {
      const emailDetail = extractErrorDetail(error);
      isProcessingPayment.value = false;
      closeAllModals();
      openPaymentModal(tariff, {
        forceEmailInput: true,
        emailErrorMessage:
          emailDetail || "Укажите email для продолжения оплаты.",
        paymentErrorMessage:
          "Для продолжения оплаты необходимо сохранить email.",
      });
      return;
    }

    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      ((error as { status?: number }).status === 403 ||
        (error as { status?: number }).status === 404)
    ) {
      handlePaymentError(errorMessage);
      return;
    }

    isProcessingPayment.value = false;
    closeAllModals();
    openPaymentModal(tariff, {
      paymentErrorMessage: errorMessage,
    });
  }
}

// Функция для запуска проверки статуса оплаты
function startPaymentStatusCheck(billId: string) {
  console.log("Начинаем проверку статуса для bill_id:", billId);

  paymentStore.startPaymentStatusPolling(
    billId,
    handlePaymentSuccess,
    handlePaymentError,
  );
}

// Функция для обработки успешной оплаты
function handlePaymentSuccess() {
  console.log("Оплата успешна, перенаправляем на /success");

  // Логируем успешное завершение оплаты в Sentry
  SentryService.setContext("payment_completed_successfully", {
    tariff: paymentStore.selectedTariff
      ? {
          id: paymentStore.selectedTariff.id,
          name: paymentStore.selectedTariff.name,
          months: paymentStore.selectedTariff.months,
          price: paymentStore.selectedTariff.price,
        }
      : null,
    payment_method: paymentStore.selectedPaymentMethod
      ? {
          id: paymentStore.selectedPaymentMethod.id,
          name: paymentStore.selectedPaymentMethod.name,
          method_code: paymentStore.selectedPaymentMethod.method_code,
        }
      : null,
    bill_id: paymentStore.currentBillId,
    redirect_page: "success",
  });

  SentryService.captureMessage("Пользователь успешно завершил оплату", "info");

  isProcessingPayment.value = false;
  closeAllModals();
  sessionStorage.setItem("paymentSuccess", "true");
  // Устанавливаем флаг для обновления данных при возврате на главную
  sessionStorage.setItem("refreshDataOnHome", "true");
  router.push("/success");
}

function isUnavailableOperationError(errorMessage: string): boolean {
  if (
    paymentStore.errorStatus === "403" ||
    paymentStore.errorStatus === "404"
  ) {
    return true;
  }

  return (
    errorMessage.toLowerCase().includes("не принадлежит") ||
    errorMessage.toLowerCase().includes("не найден")
  );
}

function handleReturnToTariffSelection() {
  unavailableOperationMessage.value = null;
  paymentStore.stopPaymentStatusPolling();
  paymentStore.clearPaymentMethods();
  isProcessingPayment.value = false;
}

// Функция для обработки ошибки оплаты
function handlePaymentError(errorMessage: string) {
  console.error("Ошибка оплаты:", errorMessage);

  if (isUnavailableOperationError(errorMessage)) {
    isProcessingPayment.value = false;
    closeAllModals();
    unavailableOperationMessage.value =
      "Операция недоступна или не найдена. Проверьте период и попробуйте снова.";
    return;
  }

  // Логируем ошибку оплаты в Sentry
  SentryService.setContext("payment_failed", {
    error_message: errorMessage,
    tariff: paymentStore.selectedTariff
      ? {
          id: paymentStore.selectedTariff.id,
          name: paymentStore.selectedTariff.name,
          months: paymentStore.selectedTariff.months,
          price: paymentStore.selectedTariff.price,
        }
      : null,
    payment_method: paymentStore.selectedPaymentMethod
      ? {
          id: paymentStore.selectedPaymentMethod.id,
          name: paymentStore.selectedPaymentMethod.name,
          method_code: paymentStore.selectedPaymentMethod.method_code,
        }
      : null,
    bill_id: paymentStore.currentBillId,
    redirect_page: "error",
  });

  SentryService.captureMessage(
    `Оплата завершилась ошибкой: ${errorMessage}`,
    "error",
  );

  isProcessingPayment.value = false;
  closeAllModals();

  if (paymentStore.selectedTariff) {
    openPaymentModal(paymentStore.selectedTariff, {
      paymentErrorMessage: errorMessage,
    });
    return;
  }

  sessionStorage.setItem("paymentError", errorMessage);
  router.push("/error");
}

function calculateEndDate(months: number, startDate?: Date): string {
  const baseDate = startDate ? new Date(startDate) : new Date();
  const endDate = new Date(baseDate);
  endDate.setMonth(endDate.getMonth() + months);

  const monthNames = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
  ];

  return `${endDate.getDate()} ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;
}

const handleBack = () => {
  // Останавливаем проверку статуса при переходе назад
  console.log("Переход назад, останавливаем проверку статуса оплаты");

  // Логируем возврат на главную во время процесса оплаты
  if (isProcessingPayment.value || paymentStore.isCheckingPayment) {
    SentryService.setContext("payment_cancelled_back_button", {
      reason: "back_button_clicked",
      tariff: paymentStore.selectedTariff
        ? {
            id: paymentStore.selectedTariff.id,
            name: paymentStore.selectedTariff.name,
            months: paymentStore.selectedTariff.months,
            price: paymentStore.selectedTariff.price,
          }
        : null,
      payment_method: paymentStore.selectedPaymentMethod
        ? {
            id: paymentStore.selectedPaymentMethod.id,
            name: paymentStore.selectedPaymentMethod.name,
            method_code: paymentStore.selectedPaymentMethod.method_code,
          }
        : null,
      bill_id: paymentStore.currentBillId,
      was_processing: isProcessingPayment.value,
      was_checking_status: paymentStore.isCheckingPayment,
    });

    SentryService.captureMessage(
      "Пользователь отменил оплату (нажал назад)",
      "info",
    );
  }

  paymentStore.stopPaymentStatusPolling();
  isProcessingPayment.value = false;

  // Если это новая подписка с выбранным сервером, возвращаемся к выбору сервера
  if (purchaseType.value === "new" && userConfirmedServerSelection.value) {
    console.log("🔙 Возврат к выбору сервера");
    userConfirmedServerSelection.value = false;
    serverStore.clearSelection();
  } else {
    // Иначе возвращаемся на главную
    router.push("/");
  }
};
</script>

<style lang="scss">
.payment-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 28px;
  box-sizing: border-box;
  overflow-y: hidden;
  overflow-x: hidden;

  &__header {
    text-align: center;
    margin-bottom: 28px;
  }

  &__main {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  &__title {
    @include h1;
    color: $neutral-100;
    margin: 0 0 16px 0;
  }

  &__subtitle {
    @include p2-regular;
    color: $neutral-60;
    margin: 0;
  }

  &__content {
    width: 100%;
    max-width: 334px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__autopayment-mode {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__mode-option {
    @include p2-regular;
    padding: 10px 12px;
    border: 1px solid $secondary-100;
    border-radius: 10px;
    color: $neutral-100;
    background: $secondary-200;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  &__mode-option--selected {
    border-color: $primary-100;
    color: $primary-100;
    background: $secondary-200-active;
  }

  &__mode-hint {
    @include p3-regular;
    color: $neutral-60;
    margin: -8px 0 12px 0;
    text-align: center;
  }

  &__loading,
  &__error {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    color: $neutral-100;
  }

  &__tariffs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  &__tariffs--updating {
    opacity: 0.75;
  }

  &__tariff-card {
    position: relative;
    background: $secondary-200;
    border-radius: 16px;
    padding: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      background: $secondary-200-active;
    }

    &--selected {
      border-color: $primary-100;
      color: $primary-100 !important;
      background: $secondary-200-active;
    }

    &--popular {
      .payment-page__tariff-label {
        @include p3-regular;
        background: $secondary-100;
        color: $neutral-100;
      }
    }

    &--profitable {
      .payment-page__tariff-label {
        background: $primary-100;
        color: $neutral-200;
      }
    }
  }

  &__tariff-label {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 16px;
    border-radius: 20px;
    font-family: "Unbounded", sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
    margin-top: auto;

    .button-base-block {
      display: flex;
      height: 48px;
    }
    .button-base {
      // height: 48px;
      justify-content: center;
      height: 100%;
    }
  }

  // Переопределяем стили BaseTariff для страницы оплаты
  .base-tariff {
    max-width: none;
    width: 100%;
    text-align: center;
    color: $neutral-100;

    &__subtitle {
      @include p2-regular;
      color: $neutral-100;
      margin-bottom: 8px;
    }

    &__days {
      font-family: "Unbounded", sans-serif;
      font-weight: 700;
      font-size: 24px;
      line-height: 30px;
      color: $neutral-100;
      margin-bottom: 16px;
    }

    &__price {
      @include h1;
      color: $neutral-100;
      margin-bottom: 4px;

      &::after {
        content: " руб.";
      }
    }

    &__perMonth {
      @include p2-regular;
      color: $neutral-60;

      &::before {
        content: "";
      }

      &::after {
        content: " руб. в месяц";
      }
    }
  }
}

.payment-page__tariff-card--selected .base-tariff__price {
  transition: all 0.3s ease;
  color: $primary-100;
}

.payment-page__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
}

.payment-page__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
  text-align: center;

  .retry-button {
    padding: 12px 24px;
    background: $primary-100;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;

    &:hover {
      background: $primary-100;
    }
  }
}
</style>
